import type { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { handleApiError, type ErrorHandlerOptions, type ApiErrorContext } from './errorHandler';

/**
 * Setup Bearer token authentication interceptor
 * Reads token from localStorage and adds to Authorization header
 */
export function setupBearerAuthInterceptor(
  client: AxiosInstance,
  tokenKey: string = 'token'
): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      const token = localStorage.getItem(tokenKey);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/**
 * Setup API Key authentication interceptor
 * Adds X-Api-Key header to all requests
 */
export function setupApiKeyAuthInterceptor(
  client: AxiosInstance,
  apiKey: string
): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      config.headers['X-Api-Key'] = apiKey;
      return config;
    },
    (error) => Promise.reject(error)
  );
}

/**
 * Login/refresh 401'i "oturum düştü" değildir — redirect + toast yanlış olur (audit M13).
 * Returns false for auth endpoints that should not trigger the unauthorized handler.
 */
export function shouldTriggerUnauthorizedHandler(url: string | undefined): boolean {
  if (!url) return true;
  return !/\/api\/auth\/(login|admin\/login|refresh)(\?|$)/.test(url);
}

/**
 * Setup error handling interceptor
 * Handles common HTTP errors (401, 403, 429, 500)
 */
export function setupErrorInterceptor(
  client: AxiosInstance,
  options?: ErrorHandlerOptions & { onUnauthorized?: () => void }
): void {
  client.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      const url = error.config?.url;
      const context: ApiErrorContext = {
        url,
        method: error.config?.method?.toUpperCase(),
      };

      // Suppress the "Oturum süreniz doldu" toast for login/refresh 401s (audit M13):
      // a wrong password on /api/auth/login is not an expired session.
      const isAuthEndpoint401 =
        error.response?.status === 401 && !shouldTriggerUnauthorizedHandler(url);
      const effectiveOptions = isAuthEndpoint401
        ? { ...options, silent: true }
        : options;

      // Handle the error
      handleApiError(error, effectiveOptions, context);

      // Special handling for 401 - unauthorized (login/refresh hariç — audit M13)
      if (
        error.response?.status === 401 &&
        options?.onUnauthorized &&
        shouldTriggerUnauthorizedHandler(url)
      ) {
        options.onUnauthorized();
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Create a request logging interceptor (useful for debugging)
 */
export function setupRequestLoggingInterceptor(
  client: AxiosInstance,
  options?: { logBody?: boolean }
): void {
  client.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      console.log(`[API] ${config.method?.toUpperCase()} ${config.url}`);
      if (options?.logBody && config.data) {
        console.log('[API] Request body:', config.data);
      }
      return config;
    }
  );
}

/**
 * Create a response logging interceptor (useful for debugging)
 */
export function setupResponseLoggingInterceptor(
  client: AxiosInstance,
  options?: { logBody?: boolean }
): void {
  client.interceptors.response.use(
    (response: AxiosResponse) => {
      console.log(`[API] ${response.status} ${response.config.url}`);
      if (options?.logBody && response.data) {
        console.log('[API] Response body:', response.data);
      }
      return response;
    }
  );
}
